import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";

// Async thunks for SMS operations
export const sendVerificationCode = createAsyncThunk(
  "sms/sendVerificationCode",
  async (phoneNumber, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/sms/verify/send", {
        phoneNumber: phoneNumber.trim(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const confirmVerificationCode = createAsyncThunk(
  "sms/confirmVerificationCode",
  async (code, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/sms/verify/confirm", {
        code: code.trim(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendSMS = createAsyncThunk(
  "sms/sendSMS",
  async ({ to, body }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/sms/send", {
        to,
        body,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  verificationCodeSent: false,
  phoneVerified: false,
  verifiedPhoneNumber: null,
  lastSentCode: null,
};

const smsSlice = createSlice({
  name: "sms",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetVerificationState: (state) => {
      state.verificationCodeSent = false;
      state.phoneVerified = false;
      state.verifiedPhoneNumber = null;
      state.lastSentCode = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Send verification code
    builder
      .addCase(sendVerificationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verificationCodeSent = false;
      })
      .addCase(sendVerificationCode.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationCodeSent = true;
        state.error = null;
      })
      .addCase(sendVerificationCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to send verification code";
        state.verificationCodeSent = false;
      });

    // Confirm verification code
    builder
      .addCase(confirmVerificationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmVerificationCode.fulfilled, (state, action) => {
        state.loading = false;
        state.phoneVerified = true;
        state.verifiedPhoneNumber = action.payload?.phoneNumber || null;
        state.error = null;
      })
      .addCase(confirmVerificationCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to verify code";
        state.phoneVerified = false;
      });

    // Send SMS
    builder
      .addCase(sendSMS.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendSMS.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(sendSMS.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to send SMS";
      });
  },
});

export const { clearError, resetVerificationState } = smsSlice.actions;
export default smsSlice.reducer;
